
import { useState, useEffect } from 'react';
import { Task, ExternalTask, UserProfile } from '../types';
import { User } from 'firebase/auth';
import { TaskService } from '../services/TaskService';

interface TaskDataProps {
    user: User | null;
    userProfile: UserProfile | null;
    effectiveTeamIds: string[];
}

// Helper to handle Firestore Timestamps vs local numbers
const getMillis = (val: any): number => {
    if (!val) return Date.now();
    if (typeof val === 'number') return val;
    if (typeof val.toMillis === 'function') return val.toMillis();
    return Date.now();
};

export const useTaskData = ({ user, userProfile, effectiveTeamIds }: TaskDataProps) => {
    const [tasks, setTasks] = useState<Task[]>([]);
    const [externalTasks, setExternalTasks] = useState<ExternalTask[]>([]);
    const [activeProjectMap, setActiveProjectMap] = useState<Record<string, string>>({});

    // 1. Listen for projectTeamMap entries for the active teams
    useEffect(() => {
        if (!user || !userProfile?.orgId || effectiveTeamIds.length === 0) {
            setActiveProjectMap({});
            return;
        }
        return TaskService.listenToProjectTeamMap(userProfile.orgId, effectiveTeamIds, setActiveProjectMap);
    }, [user, userProfile?.orgId, JSON.stringify(effectiveTeamIds)]);

    // 2. External Tasks Listener (Synced from OP/Jira/HRM)
    useEffect(() => {
        const activeProjectKeys = Object.keys(activeProjectMap);
        if (!user || !userProfile?.orgId || activeProjectKeys.length === 0) {
            setExternalTasks([]);
            return;
        }

        return TaskService.listenToExternalTasks(userProfile.orgId, activeProjectKeys, (tasksData) => {
            // UI FILTER: Only show tasks for projects that are NOT disabled by the user
            const filteredTasks = tasksData.filter(t => {
                const provider = t.externalSource;
                const disabledKeys = userProfile?.integrations?.[provider]?.disabledProjectKeys || [];
                return !disabledKeys.includes(t.projectKey);
            });

            setExternalTasks(filteredTasks);
        });
    }, [user, JSON.stringify(activeProjectMap), JSON.stringify(userProfile?.integrations)]);

    // Tasks Listener (Filtered by orgId and Active Team IDs)
    useEffect(() => {
        if (!userProfile?.orgId || effectiveTeamIds.length === 0) {
            setTasks([]);
            return;
        }

        // SCALABILITY FIX: Instead of using 'in' query (limited to 10 IDs), 
        // we query ALL tasks in the Org and filter by selected teams in the snapshot.
        return TaskService.listenToTasks(userProfile.orgId, (allOrgTasksRaw) => {
            const allOrgTasks = allOrgTasksRaw.map(data => {
                return {
                    id: data.id,
                    ...data,
                    syncing: data.syncing, // CAPTURE SYNC STATUS
                    createdAt: getMillis(data.createdAt),
                    updatedAt: getMillis(data.updatedAt),
                    startedAt: data.startedAt ? getMillis(data.startedAt) : undefined,
                    completedAt: data.completedAt ? getMillis(data.completedAt) : undefined
                } as Task;
            });

            // Filter: Only show tasks from teams the user currently has selected
            const filteredTasks = allOrgTasks.filter(t => 
                effectiveTeamIds.includes(t.teamId)
            );

            setTasks(filteredTasks);
        });
    }, [userProfile?.orgId, JSON.stringify(effectiveTeamIds)]);

    return {
        tasks,
        externalTasks,
        activeProjectMap
    };
};
